/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import { 
    TextField, MenuItem, Typography, Box, Button, Paper, Stack,
    Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid,
    IconButton, Snackbar, Table, TableBody, TableCell, TableContainer, Chip,
    TableHead, TableRow, Tabs, Tab, Checkbox, FormControlLabel,
    List, ListItem, ListItemButton, ListItemText, LinearProgress, Autocomplete,
    CircularProgress, Divider
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState, useRef } from 'react';
import { saveAs } from 'file-saver';
import EmailIcon from '@mui/icons-material/Email';
import AttachmentIcon from '@mui/icons-material/Attachment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

// 🛠️ NOTE: TabPanel Helper Component
function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const DocumentPreviewer = ({ filepath }) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(false);

    const cleanPath = filepath ? filepath.replace(/\\/g, '/').replace(/^\/+/, '') : '';
    const fullUrl = `/api/${cleanPath}`;
    const fileExtension = cleanPath.split('.').pop().toLowerCase();

    useEffect(() => {
        if (fileExtension === 'pdf' && filepath) {
            setLoading(true);
            axios.get(fullUrl, { responseType: 'blob' })
                .then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                    setBlobUrl(url);
                    setLoading(false);
                })
                .catch(err => {
                    console.error("Blob fetch failed:", err);
                    setLoading(false);
                });
        }
        return () => {
            if (blobUrl) window.URL.revokeObjectURL(blobUrl);
        };
    }, [filepath, fileExtension, fullUrl]);

    if (!filepath) return null;

    if (fileExtension === 'pdf') {
        return (
            <Box sx={{ width: '100%', height: '100%', position: 'relative', bgcolor: '#fff' }}>
                {loading ? (
                    <Stack justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2, color: 'text.secondary' }}>Loading PDF Securely...</Typography>
                    </Stack>
                ) : (
                    <iframe
                        src={blobUrl || fullUrl}
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                        title="PDF Preview"
                    />
                )}
            </Box>
        );
    } 
    
    if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension)) {
        return (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#fff', p: 1 }}>
                <img src={fullUrl} alt="preview" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4, textAlign: 'center', color: '#fff' }}>
            <Typography>Preview not available for .{fileExtension}</Typography>
        </Box>
    );
};

const AppointmentsTab = ({ matter }) => {
    // ... existing state declarations ...
    const [appointments, setAppointments] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [openEmailDialog, setOpenEmailDialog] = useState(false);
    const [outlookEmail, setOutlookEmail] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [user, setUser] = useState(null);
    
    // --- Agent Directory State ---
    const [agentDirectory, setAgentDirectory] = useState([]);
    const [directoryOpen, setDirectoryOpen] = useState(false);
    const [agentFormOpen, setAgentFormOpen] = useState(false);
    const [agentForm, setAgentForm] = useState({ id: null, name: '', contact: '', email: '', district: '' });

    // --- Document Picker State ---
    const [showDocPicker, setShowDocPicker] = useState(false);
    const [matterDocuments, setMatterDocuments] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const modalFileInputRef = useRef(null);



    // --- ADVERT STATE ---
    const [advertModalOpen, setAdvertModalOpen] = useState(false);
    const [advertModalItems, setAdvertModalItems] = useState([]);
    const [loadingAdvertItems, setLoadingAdvertItems] = useState(false);
    const [selectedAdvertItem, setSelectedAdvertItem] = useState(null);
    const [loadingForm, setLoadingForm] = useState(null);
    const [advertModalType, setAdvertModalType] = useState('form1');


    const legalMatterId = matter?.id || '';

    // 🎯 Extract Master Data for initialization 🎯
    const masterOffice = matter?.mastersOffice || matter?.magistrateOffice || matter?.MasterOffice || '';
    const masterEstateNumber = matter?.estateNo || matter?.mastersReference || matter?.estateId || matter?.ID || ''; // 🔑 FIXED: 'estateNo' is checked first

    const classification = matter?.estateClassification || '';
    const isInsolvencyOrLiquidation = ['Company', 'CC/BK', 'Person'].includes(classification);
    
    const venueOptions = ['Pretoria', 'Johannesburg', 'Cape Town', 'Durban', 'Bloemfontein', 'Polokwane', 'Pietermaritzburg', 'Gqeberha (Port Elizabeth)', 'East London', 'Kimberley', 'Mahikeng', 'Mbombela (Nelspruit)'];
    if (isInsolvencyOrLiquidation) venueOptions.unshift('Not applicable');

    const formatDateForInput = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toISOString().split('T')[0]; // Returns "YYYY-MM-DD"
    };

    const [formData, setFormData] = useState({
        magistrateOffice: masterOffice, 
        proposedDate: '',
        proposedTime: '',
        legalMatterId: legalMatterId,
        type: '',
        district: '', // New field for district/area
        minutesReceived: false,
        resolutionsAdopted: false,
        minutesDocumentUrl: null,

        // --- NEW FIELDS INITIALIZATION ---
        noticeLanguage: 'English',
        province: '',
        estateOrCompanyNumber: masterEstateNumber, 
        estateOrCompanyStatus: '',
        estateOrCompanyName: '',
        idOrRegNumber: '',
        purposeOfMeeting: '',
        annexureOrAdditionalInfo: '',
        debtPaymentPeriod: '',
        advertiserName: '',
        advertiserAddress: '',
        advertiserEmail: '',
        advertiserTelephone: '',
        dateSubmitted: formatDateForInput(new Date()),
        publicationDate: '',
        personToAttendContact: '',
        newspaperAfrikaansName: '',
        newspaperAfrikaansDate: '',
        newspaperEnglishName: '',
        newspaperEnglishDate: '',
        meetingClosed: false,
        postponementDate: '',
        postponementReason: '',
        // --- END NEW FIELDS ---
        agentAttending: '',
        personToAttend: '',
        personToAttendEmail: '',
        sendInstruction: false,
        advertisementLink: '',
    });

    // --- START OMITTED EXISTING FUNCTIONS ---

    const normalize = (path) => path ? path.replace(/\\/g, '/').toLowerCase() : "";

    const fetchDocs = async () => {
        if (!legalMatterId || legalMatterId === 'undefined') return;
        try {
            const docsRes = await axios.get(`/api/cashbook/available-documents/${legalMatterId}`);
            const allDocs = Array.isArray(docsRes.data) ? docsRes.data : [];
            setMatterDocuments(allDocs);
        } catch (err) {
            setMatterDocuments([]); 
        }
    };

    useEffect(() => {
        if (legalMatterId && showDocPicker) fetchDocs();
    }, [showDocPicker, legalMatterId]);

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDragging) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) await handleFileUploadInModal({ target: { files: [file] } });
    };
    
    const handleFileUploadInModal = async (event) => {
        const file = event.target.files[0];
        if (!file || !user?.emailAddress) return;
        setIsUploading(true);
        const uploadFormData = new FormData();
        uploadFormData.append('documents', file);
        uploadFormData.append('folder', 'Meetings');
        try {
            await axios.post(`/api/legal-matter/${legalMatterId}/uploads`, uploadFormData, { headers: { 'x-user-email': user.emailAddress, 'x-user-role': user.role } });
            await fetchDocs(); 
        } catch (error) { console.error("Upload failed", error); }
        finally { setIsUploading(false); }
    };

    useEffect(() => {
        const stored = localStorage.getItem('insollex_agent_directory');
        if (stored) {
            try {
                setAgentDirectory(JSON.parse(stored));
            } catch (e) {
                console.error("Failed to parse agent directory from localStorage", e);
            }
        }
    }, []);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const res = await axios.get('/api/user', {
                    withCredentials: true,
                });

                if (typeof res.data !== 'object' || res.data === null) {
                    console.error('Invalid user data received:', res.data);
                    setUser(null);
                    return;
                }

                if (!res.data.role) {
                    console.warn('User has no role defined. Treating as guest.');
                    res.data.role = 'guest';
                }

                setUser(res.data);
            } catch (err) {
                console.error('Could not fetch user', err);
                setUser(null);
            }
        };

        fetchUser();
    }, []);

    const formatDate = (date) => {
        if (!date) return { date: '', time: '' };

        const formattedDate = new Date(date);
        if (isNaN(formattedDate.getTime())) return { date: '', time: '' };

        const datePart = formattedDate.toISOString().split('T')[0];
        // 🛠️ Updated: Extract just the hour and format as HH:00
        const timePart = formattedDate.toTimeString().slice(0, 2) + ':00';

        return { date: datePart, time: timePart };
    };

    // Fetch appointments
    const fetchAppointments = async () => {
        try {
            let response;
            if (legalMatterId) {
                response = await axios.get(
                    `/api/appointments?legalMatterId=${legalMatterId}`
                );
            } else {
                console.warn('No legalMatterId provided. Skipping fetch.');
                return;
            }

            const data = response?.data?.data || response?.data || [];
            setAppointments(data);
        } catch (error) {
            console.error('Error fetching appointments:', error);
            setSnackbarMessage('Error fetching appointments.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    useEffect(() => {
        if (!legalMatterId) return;
        fetchAppointments();
    }, [legalMatterId]);

    // Handle form field changes
    const handleFormChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData((prev) => {
            const newState = { ...prev, [name]: type === 'checkbox' ? checked : value };
            // If meeting is closed, clear postponement fields
            if (name === 'meetingClosed' && checked) {
                newState.postponementDate = '';
                newState.postponementReason = '';
            }
            return newState;
        });
    };

    // Add new meeting
    const handleAddMeeting = () => {
        setIsEditing(false);
        setFormData({
            magistrateOffice: masterOffice, // 🎯 RESET/INIT FROM MATTER 🎯
            estateOrCompanyNumber: masterEstateNumber, // 🎯 RESET/INIT FROM MATTER 🎯
            proposedDate: '',
            proposedTime: '09:00', // Default to 9 AM
            legalMatterId,
            type: '',
            district: '',
            minutesReceived: false,
            resolutionsAdopted: false,
            minutesDocumentUrl: null,

            // --- NEW FIELDS RESET ---
            noticeLanguage: 'English',
            province: '',
            estateOrCompanyStatus: matter?.status || '',
            estateOrCompanyName: matter?.matterName || '',
            idOrRegNumber: matter?.registrationNo || matter?.idNumber || '',
            purposeOfMeeting: '',
            annexureOrAdditionalInfo: '',
            debtPaymentPeriod: '',
            advertiserName: '',
            advertiserAddress: '',
            advertiserEmail: '',
            advertiserTelephone: '',
            dateSubmitted: formatDateForInput(new Date()),
            publicationDate: '',
            personToAttendContact: '',
            newspaperAfrikaansName: '',
            newspaperAfrikaansDate: '',
            newspaperEnglishName: '',
            newspaperEnglishDate: '',
            meetingClosed: false,
            postponementDate: '',
            postponementReason: '',
            // --- END NEW FIELDS RESET ---
            agentAttending: '',
            personToAttend: '',
            personToAttendEmail: '',
            sendInstruction: false,
            advertisementLink: '',
        });
        setOpenDialog(true);
    };

    // Edit meeting
    const handleEditMeeting = (appointment) => {
        setIsEditing(true);
        setSelectedAppointment(appointment);

        const proposed = new Date(appointment.proposedDateTime);
        // Format time to 'HH:MM' for time input
        const proposedTime = proposed.toTimeString().slice(0, 5);

        setFormData({
            // 🎯 LOAD FROM APPOINTMENT OR FALLBACK TO MATTER 🎯
            magistrateOffice: appointment.magistrateOffice || masterOffice,
            // 🎯 LOAD FROM APPOINTMENT OR FALLBACK TO MATTER 🎯
            estateOrCompanyNumber: appointment.estateOrCompanyNumber || masterEstateNumber,

            proposedDate: proposed.toISOString().split('T')[0],
            proposedTime: proposedTime,
            legalMatterId: appointment.legalMatterId || '',
            type: appointment.type || '',
            district: appointment.district || '',
            minutesReceived: !!appointment.minutesReceived,
            resolutionsAdopted: !!appointment.resolutionsAdopted,
            minutesDocumentUrl: appointment.minutesDocumentUrl || null,

            // --- NEW FIELDS LOAD FROM APPOINTMENT ---
            noticeLanguage: appointment.noticeLanguage || 'English',
            province: appointment.province || '',
            estateOrCompanyStatus: appointment.estateOrCompanyStatus || matter?.status || '',
            estateOrCompanyName: appointment.estateOrCompanyName || matter?.matterName || '',
            idOrRegNumber: appointment.idOrRegNumber || matter?.registrationNo || matter?.idNumber || '',
            purposeOfMeeting: appointment.purposeOfMeeting || '',
            annexureOrAdditionalInfo: appointment.annexureOrAdditionalInfo || '',
            debtPaymentPeriod: appointment.debtPaymentPeriod || '',
            advertiserName: appointment.advertiserName || '',
            advertiserAddress: appointment.advertiserAddress || '',
            advertiserEmail: appointment.advertiserEmail || '',
            advertiserTelephone: appointment.advertiserTelephone || '',
            dateSubmitted: formatDateForInput(appointment.dateSubmitted || new Date()),
            publicationDate: formatDateForInput(appointment.publicationDate),
            personToAttendContact: appointment.personToAttendContact || '',
            newspaperAfrikaansName: appointment.newspaperAfrikaansName || '',
            newspaperAfrikaansDate: formatDateForInput(appointment.newspaperAfrikaansDate),
            newspaperEnglishName: appointment.newspaperEnglishName || '',
            newspaperEnglishDate: formatDateForInput(appointment.newspaperEnglishDate),
            meetingClosed: !!appointment.meetingClosed,
            postponementDate: formatDateForInput(appointment.postponementDate),
            postponementReason: appointment.postponementReason || '',
            // --- END NEW FIELDS LOAD ---
            agentAttending: appointment.agentAttending || '',
            personToAttend: appointment.personToAttend || '',
            personToAttendEmail: appointment.personToAttendEmail || '',
            sendInstruction: !!appointment.sendInstruction,
            advertisementLink: appointment.advertisementLink || '',
        });

        setOpenDialog(true);
    };

    // Save meeting (add or update)
    const handleSaveMeeting = async () => {
        try {
            // --- CHRONOLOGICAL VALIDATION ---
            const newMeetingType = formData.type;
            const newMeetingDate = new Date(formData.proposedDate);
            newMeetingDate.setHours(0, 0, 0, 0); // Normalize date for comparison

            // Filter out the meeting being edited from the list to compare against
            const otherAppointments = appointments.filter(appt => !isEditing || appt.id !== selectedAppointment.id);

            if (newMeetingType === 'secondMeeting') {
                const firstMeeting = otherAppointments.find(appt => appt.type === 'firstMeeting' || appt.type === 'firstAndSecondMeeting');
                if (firstMeeting) {
                    const firstMeetingDate = new Date(firstMeeting.proposedDateTime);
                    firstMeetingDate.setHours(0, 0, 0, 0);
                    if (newMeetingDate < firstMeetingDate) {
                        setSnackbarMessage('Error: Second Meeting cannot be scheduled before the First Meeting.');
                        setSnackbarSeverity('error');
                        setSnackbarOpen(true);
                        return; // Stop execution
                    }
                }
            } else if (newMeetingType === 'firstMeeting') {
                const secondMeeting = otherAppointments.find(appt => appt.type === 'secondMeeting');
                if (secondMeeting) {
                    const secondMeetingDate = new Date(secondMeeting.proposedDateTime);
                    secondMeetingDate.setHours(0, 0, 0, 0);
                    if (newMeetingDate > secondMeetingDate) {
                        setSnackbarMessage('Error: First Meeting cannot be scheduled after the Second Meeting.');
                        setSnackbarSeverity('error');
                        setSnackbarOpen(true);
                        return; // Stop execution
                    }
                }
            }

            // Constructing DateTime using selected date and selected hour time (HH:00:00.000Z)
            const proposedDateTime = `${formData.proposedDate}T${formData.proposedTime || '00:00'}:00`;
            // 🛠️ Using Proposed Date/Time for Due Date/Time for now, as Due Date/Time fields are removed from the UI
            const dueDateTime = proposedDateTime;

            // 🎯 FIX: Ensure all fields from the form are included in the payload
            const dataToSave = {
                ...formData,
                proposedDateTime,
                dueDateTime,
                magistrateOffice: formData.magistrateOffice,
                type: formData.type,
                purposeOfMeeting: formData.purposeOfMeeting,
                personToAttend: formData.personToAttend,
                personToAttendEmail: formData.personToAttendEmail,
                personToAttendContact: formData.personToAttendContact,
                district: formData.district,
                agentAttending: formData.agentAttending,
                sendInstruction: !!formData.sendInstruction,
                minutesReceived: !!formData.minutesReceived,
                minutesDocumentUrl: formData.minutesDocumentUrl,
                meetingClosed: !!formData.meetingClosed, // resolutionsAdopted is already in formData
                // ... and all other formData fields are spread ...
            };

            // --- 🎯 Auto-save Agent to Directory ---
            if (formData.personToAttend && formData.personToAttendEmail) {
                const updatedDirectory = [...agentDirectory];
                const existingAgentIndex = updatedDirectory.findIndex(
                    (agent) => agent.email.toLowerCase() === formData.personToAttendEmail.toLowerCase()
                );

                const agentData = {
                    name: formData.personToAttend,
                    email: formData.personToAttendEmail,
                    contact: formData.personToAttendContact || '',
                    district: formData.district || '',
                };

                if (existingAgentIndex > -1) {
                    // Update existing agent
                    updatedDirectory[existingAgentIndex] = { ...updatedDirectory[existingAgentIndex], ...agentData };
                } else {
                    // Add new agent
                    updatedDirectory.push({ ...agentData, id: -Math.floor(Math.random() * 1000000) });
                }
                setAgentDirectory(updatedDirectory);
                localStorage.setItem('insollex_agent_directory', JSON.stringify(updatedDirectory));
            }

            if (isEditing) {
                await axios.put(
                    `/api/appointments/${selectedAppointment.id}`,
                    dataToSave
                );
                setSnackbarMessage('Appointment updated successfully!');
            } else {
                const res = await axios.post('/api/appointments', dataToSave);
                setAppointments((prev) => [...prev, res.data]);
                setSnackbarMessage('Appointment added successfully!');
            }

            setSnackbarSeverity('success');
            setOpenDialog(false);
            fetchAppointments();
        } catch (error) {
            console.error(error);
            setSnackbarMessage('Error saving appointment.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleEmailChange = (e) => {
        setOutlookEmail(e.target.value);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleDeleteAppointment = async (id) => {
        try {
            await axios.delete(`/api/appointments/${id}`);
            setAppointments((prev) => prev.filter((a) => a.id !== id));
            setSnackbarMessage('Appointment deleted successfully!');
            setSnackbarSeverity('success');
        } catch {
            setSnackbarMessage('Error deleting appointment.');
            setSnackbarSeverity('error');
        } finally {
            setSnackbarOpen(true);
        }
    };

    const handleSendToOutlook = () => {
        setOpenEmailDialog(true);
    };

    const handleSubmitEmail = async () => {
        if (!outlookEmail || !outlookEmail.includes('@')) {
            setSnackbarMessage('Please enter a valid email address.');
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
            return;
        }

        const meetings = appointments
            .map((a) => {
                const start = new Date(a.proposedDateTime);
                const end = new Date(a.dueDateTime);
                if (isNaN(start) || isNaN(end)) return null;

                return {
                    subject: `Meeting – ${a.meetingName}`,
                    description: `Scheduled meeting regarding ${a.meetingName} at ${a.magistrateOffice}.`,
                    location: a.magistrateOffice || 'Online',
                    startTime: [
                        start.getFullYear(),
                        start.getMonth() + 1,
                        start.getDate(),
                        start.getHours(),
                        start.getMinutes(),
                    ],
                    endTime: [
                        end.getFullYear(),
                        end.getMonth() + 1,
                        end.getDate(),
                        end.getHours(),
                        end.getMinutes(),
                    ],
                };
            })
            .filter(Boolean);

        if (!meetings.length) {
            setSnackbarMessage('No valid appointments with dates found.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        try {
            await axios.post('/api/send-outlook-meeting', {
                email: outlookEmail,
                meetings,
            });

            setSnackbarMessage('All appointments sent to Outlook successfully!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setOpenEmailDialog(false);
        } catch (err) {
            console.error('Failed to send appointments:', err.response?.data || err);
            const errorMessage =
                err?.response?.data?.message || 'Failed to send Outlook invites.';
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleToggleMinutes = async (appointmentId, value) => {
        try {
            await axios.put(`/api/appointments/${appointmentId}`, {
                minutesReceived: value,
            });
            setAppointments((prev) =>
                prev.map((a) => (a.id === appointmentId ? { ...a, minutesReceived: value } : a))
            );
        } catch (error) {
            console.error('Error updating minutes received:', error);
        }
    };

    const handleCloseEmailDialog = () => {
        setOpenEmailDialog(false);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

     const meetingTypeMap = {
        firstMeeting: 'First Meeting',
        secondMeeting: 'Second Meeting',
        firstAndSecondMeeting: 'First & Second Meeting',
        generalMeeting: 'General Meeting',
        specialMeeting: 'Special Meeting',
     };

    // --- Agent Directory Handlers ---
    const handleOpenDirectory = () => setDirectoryOpen(true);
    const handleCloseDirectory = () => setDirectoryOpen(false);

    const handleOpenAgentForm = (agent = null) => {
        if (agent) {
            setAgentForm({ id: agent.id, name: agent.name, contact: agent.contact, email: agent.email, district: agent.district });
        } else {
            setAgentForm({ id: null, name: '', contact: '', email: '', district: '' });
        }
        setAgentFormOpen(true);
    };

    const handleCloseAgentForm = () => setAgentFormOpen(false);

    const handleSaveAgent = () => {
        let updatedDirectory = [...agentDirectory];
        if (agentForm.id) { // Editing existing
            updatedDirectory = updatedDirectory.map(a => a.id === agentForm.id ? agentForm : a);
        } else { // Adding new
            updatedDirectory.push({ ...agentForm, id: -Math.floor(Math.random() * 1000000) });
        }
        setAgentDirectory(updatedDirectory);
        localStorage.setItem('insollex_agent_directory', JSON.stringify(updatedDirectory));
        handleCloseAgentForm();
    };

    const handleDeleteAgent = (id) => {
        if (window.confirm("Are you sure you want to delete this contact from the directory?")) {
            const updatedDirectory = agentDirectory.filter(a => a.id !== id);
            setAgentDirectory(updatedDirectory);
            localStorage.setItem('insollex_agent_directory', JSON.stringify(updatedDirectory));
        }
    };
    // --- ADVERT LOGIC START (MEETING ADVERT ONLY) ---

    const handleOpenAdvertModal = async (formType) => {
        setAdvertModalType(formType || 'j29');
        setAdvertModalItems([]);
        setSelectedAdvertItem(null);
        setAdvertModalOpen(false);

        setLoadingAdvertItems(true);
        setAdvertModalOpen(true);

        try {
            const url = `/api/appointments?legalMatterId=${legalMatterId}`;

            const response = await axios.get(url);
            const items = (response.data.data || response.data || []).map((item) => {
                const date = item.proposedDateTime ? new Date(item.proposedDateTime) : null;
                return {
                    id: item.id || item.ID,
                    type: item.type || 'meeting',
                    name: `${meetingTypeMap[item.type] || 'Meeting'} - ${
                        date ? date.toLocaleDateString() : 'No Date'
                    }`,
                };
            });

            setAdvertModalItems(items);
        } catch (error) {
            console.error('Error fetching data for advert:', error);
            setSnackbarMessage('Error fetching data for notice.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setAdvertModalOpen(false);
        } finally {
            setLoadingAdvertItems(false);
        }
    };

    const handleSelectAdvertItem = (item, formName) => {
        setSelectedAdvertItem(item);
        handleGenerateAdvertPdf(item, formName);
    };

    const handleCloseAdvertModal = () => {
        setAdvertModalOpen(false);
        setSelectedAdvertItem(null);
    };

    // NOTE: Updated the template map to reflect the new structure.
    const handleGenerateAdvertPdf = async (selection, formName) => {
        let targetFormName = formName;
        const targetTemplateMap = {
            form1: ['Meeting Advert.docx'],
            j29: ['J29 First Meeting Notice.docx'],
            form2: ['Form 2 Second Meeting Notice.docx'],
        };

        const templateFiles = targetTemplateMap[targetFormName];

        if (!legalMatterId) {
            setSnackbarMessage('Legal Matter not found');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            handleCloseAdvertModal();
            return;
        }

        if (!templateFiles || templateFiles.length === 0) {
            setSnackbarMessage(`No template defined for ${targetFormName.toUpperCase()}.`);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            handleCloseAdvertModal();
            return;
        }


        setLoadingForm(targetFormName);
        handleCloseAdvertModal();

        try {
            const BASE_URL = window.location.origin;

            let selectionParam = selection?.id || '';

            for (const templateFileName of templateFiles) {
                let idToUse;
                let pathType;

                if (legalMatterId) {
                    idToUse = legalMatterId;
                    pathType = 'legalMatter';
                } else {
                    continue;
                }

                const url = `${BASE_URL}/api/pdf/${pathType}/${idToUse}?templateName=${encodeURIComponent(
                    templateFileName
                )}&selection=${encodeURIComponent(selectionParam)}`;

                try {
                    const response = await axios.get(url, { responseType: 'blob' });
                    const blob = new Blob([response.data], { type: 'application/pdf' });

                    const cleanName = templateFileName.replace('.docx', '').replace(/\s+/g, '_');
                    const fileName = `${targetFormName}_${idToUse}_${cleanName}_${selectionParam}.pdf`;

                    saveAs(blob, fileName);

                    setSnackbarMessage(`PDF generated successfully: ${fileName}`);
                    setSnackbarSeverity('success');
                    setSnackbarOpen(true);
                } catch (downloadError) {
                    console.error(`[PDF] Error downloading template ${templateFileName}:`, downloadError);
                    setSnackbarMessage(
                        `Failed to download template ${templateFileName}: ${
                            downloadError?.response?.data?.message ||
                            downloadError?.message ||
                            'Server error'
                        }`
                    );
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                }
            }
        } catch (err) {
            console.error('[PDF] Unexpected error:', err);
            setSnackbarMessage(
                `Unexpected error generating ${targetFormName.toUpperCase()}: ${err?.message || err}`
            );
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setLoadingForm(null);
        }
    };

    // --- ADVERT LOGIC END ---

    return (
        // Outer Box maintains full width
        <Box sx={{ width: '100%', p: 3 }}>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="flex-end" gap={2} mb={1}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleAddMeeting}
                            disabled={user?.role === 'guest'}
                        >
                            Schedule Meeting
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            onClick={handleSendToOutlook}
                            disabled={user?.role === 'guest'}
                        >
                            Send Meetings to my Outlook
                        </Button>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 1 }} align="center">
                        Scheduled Meetings
                    </Typography>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Meeting Type</strong></TableCell>
                                    <TableCell><strong>Masters/Magistrate Office</strong></TableCell>
                                    <TableCell><strong>Schedule Date & Time</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell><strong>Minutes</strong></TableCell>
                                    <TableCell><strong>Resolutions</strong></TableCell>
                                    <TableCell><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {appointments.map((appointment) => {
                                    const proposedDateTime = appointment.proposedDateTime ? new Date(appointment.proposedDateTime) : null;
                                    
                                    const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: false };
                                    
                                    const proposedStr = proposedDateTime && !isNaN(proposedDateTime.getTime())
                                        ? `${proposedDateTime.toLocaleDateString('en-ZA')} ${proposedDateTime.toLocaleTimeString('en-GB', timeOptions)}`
                                        : '';
                                        
                                    return (
                                        <TableRow key={appointment.id}>
                                            <TableCell>{meetingTypeMap[appointment.type] || appointment.type}</TableCell>
                                            <TableCell>{appointment.magistrateOffice}</TableCell>
                                            <TableCell>{proposedStr}</TableCell>
                                            <TableCell>
                                                {appointment.meetingClosed ? (
                                                    <Typography variant="body2" color="success.main" fontWeight="bold">Closed</Typography>
                                                ) : appointment.postponementDate && appointment.postponementDate.trim() !== '' ? (
                                                    <Typography variant="body2" color="error.main" fontWeight="bold">Postponed</Typography>
                                                ) : (
                                                    <Typography variant="body2" color="warning.main" fontWeight="bold">Open</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={appointment.minutesReceived ? 'Obtained' : 'Pending'}
                                                    color={appointment.minutesReceived ? 'success' : 'default'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                                {appointment.minutesDocumentUrl && <Button size="small" onClick={() => window.open(`/api/${appointment.minutesDocumentUrl.replace(/\\/g, '/')}`, '_blank')}>View</Button>}
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={appointment.resolutionsAdopted ? 'Adopted' : 'Pending'}
                                                    color={appointment.resolutionsAdopted ? 'success' : 'default'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                                {appointment.resolutionsDocumentUrl && <Button size="small" onClick={() => window.open(`/api/${appointment.resolutionsDocumentUrl.replace(/\\/g, '/')}`, '_blank')}>View</Button>}
                                            </TableCell>
                                            <TableCell>
                                                <Button color="primary" onClick={() => handleEditMeeting(appointment)}>Edit</Button>
                                                <IconButton color="error" onClick={() => handleDeleteAppointment(appointment.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
            
            {/* Add/Edit Meeting Dialog */}
<Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
    <DialogTitle>{isEditing ? 'Edit Meeting' : 'Schedule New Meeting'}</DialogTitle>
    <DialogContent>
        {/* 1. MEETING INFORMATION */}
        <Typography variant="subtitle1" color="primary.main" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Meeting Information</Typography>
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField
                    select label="Type of Meeting" fullWidth size="small"
                    name="type" value={formData.type} onChange={handleFormChange}
                >
                    <MenuItem value="firstMeeting">First Meeting</MenuItem>
                    <MenuItem value="secondMeeting">Second Meeting</MenuItem>
                    <MenuItem value="firstAndSecondMeeting">First & Second Meeting</MenuItem>
                    <MenuItem value="generalMeeting">General Meeting</MenuItem>
                    <MenuItem value="specialMeeting">Special Meeting</MenuItem>
                </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    label="Meeting Venue (Master / Magistrate)"
                    fullWidth
                    size="small"
                    name="magistrateOffice"
                    value={formData.magistrateOffice || ''}
                    onChange={handleFormChange}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField label="Meeting Date" type="date" fullWidth size="small" name="proposedDate" value={formData.proposedDate} onChange={handleFormChange} InputLabelProps={{ shrink: true }} inputProps={{ max: '9999-12-31' }} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField label="Meeting Time" type="time" fullWidth size="small" name="proposedTime" value={formData.proposedTime} onChange={handleFormChange} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    select
                    label="Reason for Meeting"
                    fullWidth
                    size="small"
                    name="purposeOfMeeting"
                    value={formData.purposeOfMeeting}
                    onChange={handleFormChange}
                >
                    <MenuItem value="Proof of Claims">Proof of Claims</MenuItem>
                    <MenuItem value="Election of Trustee/Liquidator/Judicial Manager">Election of Trustee/Liquidator/Judicial Manager</MenuItem>
                    <MenuItem value="Discussion of Report">Discussion of Report</MenuItem>
                    <MenuItem value="Directions by Creditors">Directions by Creditors</MenuItem>
                    <MenuItem value="Interrogation of Witness">Interrogation of Witness</MenuItem>
                    <MenuItem value="Proof of Claims & Adopting Resolutions">Proof of Claims & Adopting Resolutions</MenuItem>
                </TextField>
            </Grid>
            <Grid item xs={12}>
                <TextField label="Additional Information (Annexure)" fullWidth multiline rows={2} size="small" name="annexureOrAdditionalInfo" value={formData.annexureOrAdditionalInfo} onChange={handleFormChange} />
            </Grid>
        </Grid>

        {/* 2. ATTENDANCE & INSTRUCTION */}
        <Typography variant="subtitle1" color="primary.main" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>Attendance & Instruction</Typography>
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField label="Agent Attending" fullWidth size="small" name="agentAttending" value={formData.agentAttending} onChange={handleFormChange} />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                    control={<Checkbox checked={formData.sendInstruction || false} onChange={handleFormChange} name="sendInstruction" color="primary" />}
                    label="Instruction to Attend (Email Dispatch)"
                />
            </Grid>
            <Grid item xs={12}>
                <Autocomplete
                    options={agentDirectory}
                    getOptionLabel={(option) => `${option.name} (${option.district || 'N/A'})`}
                    onChange={(e, value) => value && setFormData(prev => ({ ...prev, personToAttend: value.name, personToAttendEmail: value.email, personToAttendContact: value.contact, district: value.district }))}
                    renderInput={(params) => <TextField {...params} label="Load from Directory..." size="small" />}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <TextField label="Full Name & Surname" fullWidth size="small" name="personToAttend" value={formData.personToAttend} onChange={handleFormChange} />
            </Grid>
            <Grid item xs={12} sm={4}>
                <TextField label="Email Address" type="email" fullWidth size="small" name="personToAttendEmail" value={formData.personToAttendEmail} onChange={handleFormChange} />
            </Grid>
            <Grid item xs={12} sm={4}>
                <TextField label="Contact Details" fullWidth size="small" name="personToAttendContact" value={formData.personToAttendContact} onChange={handleFormChange} />
            </Grid>
            <Grid item xs={12} sm={4}>
                <TextField label="District / Area" fullWidth size="small" name="district" value={formData.district} onChange={handleFormChange} />
            </Grid>
        </Grid>

        {/* 3. ADVERTISEMENT INFORMATION */}
        <Typography variant="subtitle1" color="primary.main" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>Advertisement Information</Typography>
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField label="Government Gazette – Date" type="date" fullWidth size="small" name="publicationDate" value={formData.publicationDate} onChange={handleFormChange} InputLabelProps={{ shrink: true }} inputProps={{ max: '9999-12-31' }} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField label="Newspaper (Afrikaans) – Name" fullWidth size="small" name="newspaperAfrikaansName" value={formData.newspaperAfrikaansName} onChange={handleFormChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField label="Newspaper (Afrikaans) – Date" type="date" fullWidth size="small" name="newspaperAfrikaansDate" value={formData.newspaperAfrikaansDate} onChange={handleFormChange} InputLabelProps={{ shrink: true }} inputProps={{ max: '9999-12-31' }} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField label="Newspaper (English) – Name" fullWidth size="small" name="newspaperEnglishName" value={formData.newspaperEnglishName} onChange={handleFormChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField label="Newspaper (English) – Date" type="date" fullWidth size="small" name="newspaperEnglishDate" value={formData.newspaperEnglishDate} onChange={handleFormChange} InputLabelProps={{ shrink: true }} inputProps={{ max: '9999-12-31' }} />
            </Grid>
        </Grid>

        {/* 4. MEETING STATUS */}
        <Typography variant="subtitle1" color="primary.main" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>Meeting Status</Typography>
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <FormControlLabel
                    control={<Checkbox checked={formData.meetingClosed} onChange={handleFormChange} name="meetingClosed" color="primary" />}
                    label="Meeting has been closed"
                />
            </Grid>
            {!formData.meetingClosed && (
                <>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Postponement Date" type="date" fullWidth size="small" name="postponementDate" value={formData.postponementDate} onChange={handleFormChange} InputLabelProps={{ shrink: true }} inputProps={{ max: '9999-12-31' }} />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <TextField label="Postponement Reason" fullWidth size="small" name="postponementReason" value={formData.postponementReason} onChange={handleFormChange} />
                    </Grid>
                </>
            )}
        </Grid>

        {/* 5. MINUTES & RESOLUTIONS */}
        <Typography variant="subtitle1" color="primary.main" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>Minutes & Resolutions</Typography>
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <FormControlLabel
                        control={<Checkbox checked={formData.minutesReceived} onChange={handleFormChange} name="minutesReceived" color="primary" />}
                        label="Minutes have been obtained"
                    />
                    <IconButton color={formData.minutesDocumentUrl ? "success" : "primary"} onClick={() => { setFormData(prev => ({ ...prev, docPickerTarget: 'minutes' })); setShowDocPicker(true); }}>
                        {formData.minutesDocumentUrl ? <VisibilityIcon /> : <AttachmentIcon />}
                    </IconButton>
                </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <FormControlLabel
                        control={<Checkbox checked={formData.resolutionsAdopted} onChange={handleFormChange} name="resolutionsAdopted" color="primary" />}
                        label="Resolutions have been adopted"
                    />
                </Stack>
            </Grid>
        </Grid>
    </DialogContent>
    <DialogActions>
        <Button onClick={handleCloseDialog} color="error">
            Cancel
        </Button>
        <Button onClick={handleSaveMeeting} color="primary" variant="contained">
            {isEditing ? 'Save Changes' : 'Add Meeting'}
        </Button>
    </DialogActions>
</Dialog>
            
            {/* Document Picker Modal */}
            <Dialog open={showDocPicker} onClose={() => setShowDocPicker(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            {formData.docPickerTarget === 'minutes' 
                                ? 'Link Meeting Minutes' 
                                : 'Link Adopted Resolutions'}
                        </Typography>
                        <Box>
                            <input type="file" ref={modalFileInputRef} hidden onChange={handleFileUploadInModal} />
                            <Button variant="contained" size="small" startIcon={<CloudUploadIcon />} onClick={() => modalFileInputRef.current.click()} disabled={isUploading}>
                                {isUploading ? "Uploading..." : "Upload New"}
                            </Button>
                        </Box>
                    </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 0, height: '600px', display: 'flex' }}>
                    <Box 
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        sx={{ width: '30%', borderRight: '1px solid #ddd', overflowY: 'auto', bgcolor: isDragging ? '#e3f2fd' : '#f9f9f9' }}
                    >
                        <List>
                            {matterDocuments.length === 0 ? (
                                <Typography align="center" sx={{ py: 4, color: 'text.secondary' }}>No documents found.</Typography>
                            ) : (
                                matterDocuments.map((doc) => {
                                    const docPath = normalize(doc.filepath || doc.fileUrl);
                                    const activeDoc = formData.docPickerTarget === 'minutes' ? formData.minutesDocumentUrl : formData.resolutionsDocumentUrl;
                                    const isSelected = normalize(activeDoc) === docPath;

                                    return (
                                        <ListItem 
                                            button 
                                            key={doc.id} 
                                            selected={isSelected}
                                            onClick={() => {
                                                const fieldToUpdate = formData.docPickerTarget === 'minutes' 
                                                    ? 'minutesDocumentUrl' 
                                                    : 'resolutionsDocumentUrl';
                                                setFormData(prev => ({ ...prev, [fieldToUpdate]: doc.filepath }));
                                            }}
                                            sx={{ borderBottom: '1px solid #eee' }}
                                        >
                                            <ListItemText 
                                                primary={doc.fileName || doc.name} 
                                                secondary={`Date: ${formatDateForInput(doc.uploadDate)}`}
                                            />
                                        </ListItem>
                                    );
                                })
                            )}
                        </List>
                    </Box>
                    <Box sx={{ width: '70%', height: '100%', bgcolor: '#525659' }}>
                        {(formData.docPickerTarget === 'minutes' ? formData.minutesDocumentUrl : formData.resolutionsDocumentUrl) ? (
                            <DocumentPreviewer filepath={formData.docPickerTarget === 'minutes' ? formData.minutesDocumentUrl : formData.resolutionsDocumentUrl} />
                        ) : (
                            <Stack justifyContent="center" alignItems="center" sx={{ height: '100%', color: '#fff' }}>
                                <VisibilityIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
                                <Typography>Select a document to preview</Typography>
                            </Stack>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Send to Outlook Email Dialog (Unchanged) */}
            <Dialog open={openEmailDialog} onClose={handleCloseEmailDialog} fullWidth maxWidth="xs">
                <DialogTitle>Send Meetings to Outlook Calendar</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Enter your email address to receive .ics files for all scheduled meetings.
                    </Typography>
                    <TextField
                        label="Outlook Email Address"
                        type="email"
                        fullWidth
                        margin="dense"
                        value={outlookEmail}
                        onChange={handleEmailChange}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSubmitEmail} color="primary" variant="contained">
                        Send
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Advert Item Selection Modal (Unchanged) */}
            <Dialog open={advertModalOpen} onClose={handleCloseAdvertModal} fullWidth maxWidth="sm">
                <DialogTitle>
                    Select Meeting for {advertModalType === 'j29' ? 'Form J29 Notice' : 'Form 2 Notice'}
                </DialogTitle>
                <DialogContent>
                    {loadingAdvertItems ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                            <LinearProgress sx={{ width: '80%' }} />
                        </Box>
                    ) : advertModalItems.length === 0 ? (
                        <Alert severity="warning">No meetings found to generate the notice.</Alert>
                    ) : (
                        <List component={Paper} elevation={1} sx={{ maxHeight: 400, overflow: 'auto' }}>
                            {advertModalItems.map((item) => (
                                <ListItem key={item.id} disablePadding>
                                    <ListItemButton onClick={() => handleSelectAdvertItem(item, advertModalType)}>
                                        <ListItemText primary={item.name} secondary={`Type: ${item.type}`} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAdvertModal} color="error">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Agent Directory Dialog */}
            <Dialog open={directoryOpen} onClose={handleCloseDirectory} fullWidth maxWidth="md">
                <DialogTitle>Agent Directory</DialogTitle>
                <DialogContent>
                    <Button startIcon={<AddCircleOutlineIcon />} onClick={() => handleOpenAgentForm()} sx={{ mb: 2 }}>
                        Add New Agent
                    </Button>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>District/Area</TableCell>
                                    <TableCell>Contact</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {agentDirectory.map(agent => (
                                    <TableRow key={agent.id}>
                                        <TableCell>{agent.name}</TableCell>
                                        <TableCell>{agent.district}</TableCell>
                                        <TableCell>{agent.contact}</TableCell>
                                        <TableCell>{agent.email}</TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => handleOpenAgentForm(agent)}><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={() => handleDeleteAgent(agent.id)}><DeleteIcon fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDirectory}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Agent Form Dialog */}
            <Dialog open={agentFormOpen} onClose={handleCloseAgentForm} fullWidth maxWidth="sm">
                <DialogTitle>{agentForm.id ? 'Edit Agent' : 'Add New Agent'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Full Name" value={agentForm.name} onChange={e => setAgentForm({ ...agentForm, name: e.target.value })} /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="District / Area" value={agentForm.district} onChange={e => setAgentForm({ ...agentForm, district: e.target.value })} /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Contact Number" value={agentForm.contact} onChange={e => setAgentForm({ ...agentForm, contact: e.target.value })} /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Email Address" value={agentForm.email} onChange={e => setAgentForm({ ...agentForm, email: e.target.value })} /></Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAgentForm}>Cancel</Button>
                    <Button onClick={handleSaveAgent} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for Notifications (Unchanged) */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>

        </Box>
    );
};

export default AppointmentsTab;